import { useEffect, useState } from 'react'
import { CloudOff, Cloud, RefreshCw, Check, AlertTriangle, X, RotateCcw, Trash2 } from 'lucide-react'
import {
  pendingCount,
  quarantinedCount,
  subscribe,
  replayQueue,
  listQuarantined,
  requeueItem,
  discardItem,
} from '../services/offlineQueue'
import api, { uploadPinPhoto } from '../services/api'
// Binary photo queue (pasture pins) — counted and flushed alongside the
// JSON queue so the badge tells the whole offline story.
import {
  pendingPhotoCount,
  quarantinedPhotoCount,
  listPhotoQueue,
  flushPhotoQueue,
  retryQuarantined as retryPhoto,
  discardPhoto,
} from '../lib/photoQueue'

export default function SyncStatus() {
  const [online, setOnline] = useState(navigator.onLine)
  const [pending, setPending] = useState(0)
  const [quarantined, setQuarantined] = useState(0)
  const [photoPending, setPhotoPending] = useState(0)
  const [photoQuarantined, setPhotoQuarantined] = useState(0)
  const [syncing, setSyncing] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [quarantinedItems, setQuarantinedItems] = useState([])
  const [quarantinedPhotos, setQuarantinedPhotos] = useState([])
  const [itemsLoading, setItemsLoading] = useState(false)

  useEffect(() => {
    const refresh = async () => {
      setOnline(navigator.onLine)
      try {
        setPending(await pendingCount())
        setQuarantined(await quarantinedCount())
      } catch {
        setPending(0)
        setQuarantined(0)
      }
      try {
        setPhotoPending(await pendingPhotoCount())
        setPhotoQuarantined(await quarantinedPhotoCount())
      } catch {
        setPhotoPending(0)
        setPhotoQuarantined(0)
      }
    }
    refresh()
    const unsub = subscribe(refresh)
    window.addEventListener('photo-queue-changed', refresh)
    return () => {
      unsub()
      window.removeEventListener('photo-queue-changed', refresh)
    }
  }, [])

  const loadQuarantined = async () => {
    setItemsLoading(true)
    try {
      setQuarantinedItems(await listQuarantined())
      setQuarantinedPhotos((await listPhotoQueue()).filter((p) => p.quarantined))
    } finally {
      setItemsLoading(false)
    }
  }

  useEffect(() => {
    if (showModal) loadQuarantined()
  }, [showModal])

  const triggerSync = async () => {
    if (!navigator.onLine) return
    setSyncing(true)
    try {
      await replayQueue(api)
      await flushPhotoQueue(uploadPinPhoto)
      setPending(await pendingCount())
      setQuarantined(await quarantinedCount())
      setPhotoPending(await pendingPhotoCount())
      setPhotoQuarantined(await quarantinedPhotoCount())
    } finally {
      setSyncing(false)
    }
  }

  const handleBadgeClick = () => {
    if (quarantined > 0) {
      setShowModal(true)
    } else {
      triggerSync()
    }
  }

  const handleRetry = async (id) => {
    await requeueItem(id)
    await loadQuarantined()
    setQuarantined(await quarantinedCount())
    if (navigator.onLine) triggerSync()
  }

  const handleDiscard = async (id) => {
    await discardItem(id)
    await loadQuarantined()
    setQuarantined(await quarantinedCount())
  }

  const handleRetryPhoto = async (id) => {
    await retryPhoto(id)
    await loadQuarantined()
    setPhotoQuarantined(await quarantinedPhotoCount())
    if (navigator.onLine) triggerSync()
  }

  const handleDiscardPhoto = async (id) => {
    await discardPhoto(id)
    await loadQuarantined()
    setPhotoQuarantined(await quarantinedPhotoCount())
  }

  const totalPending = pending + photoPending
  const totalQuarantined = quarantined + photoQuarantined

  if (online && totalPending === 0 && totalQuarantined === 0) {
    return null
  }

  const hasFailures = totalQuarantined > 0
  const color = !online
    ? 'bg-amber-50 text-amber-700 hover:bg-amber-100'
    : hasFailures
    ? 'bg-red-50 text-red-700 hover:bg-red-100'
    : totalPending > 0
    ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
    : 'bg-green-50 text-green-700'

  const photoNote = photoPending > 0 ? ` (incl. ${photoPending} photo(s))` : ''
  const title = !online
    ? `Offline — ${totalPending} change(s) will sync when online${photoNote}` +
      (hasFailures ? `; ${totalQuarantined} failed item(s) — click to review` : '')
    : hasFailures
    ? `${totalQuarantined} item(s) rejected by the server — click to review` +
      (totalPending > 0 ? `; ${totalPending} still pending` : '')
    : `${totalPending} change(s) pending${photoNote} — click to sync now`

  const Icon = !online
    ? CloudOff
    : syncing
    ? RefreshCw
    : hasFailures
    ? AlertTriangle
    : totalPending > 0
    ? Cloud
    : Check

  const label = !online
    ? 'Offline'
    : syncing
    ? 'Syncing'
    : hasFailures
    ? 'Failed'
    : 'Sync'

  return (
    <>
      <button
        onClick={handleBadgeClick}
        disabled={(!online && !hasFailures) || syncing}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${color}`}
        title={title}
      >
        <Icon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
        <span>
          {label}
          {(totalPending > 0 || totalQuarantined > 0) && (
            <span className="ml-1 font-bold">
              ({totalPending + totalQuarantined})
            </span>
          )}
        </span>
      </button>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Failed syncs</h2>
                  <p className="text-sm text-gray-500">
                    These writes were rejected by the server and are not in the system
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg text-gray-500"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {!itemsLoading && quarantinedPhotos.map((p) => (
                <div key={`photo-${p.id}`} className="border border-red-100 rounded-lg p-4 bg-red-50/30">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">
                        Pin photo ({p.slot}) — {(p.size / 1024).toFixed(0)} KB
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Queued {new Date(p.queuedAt).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  {p.lastError && (
                    <div className="text-sm text-red-700 bg-white border border-red-100 rounded px-3 py-2 mb-3 font-mono break-all">
                      {p.lastError}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={() => handleRetryPhoto(p.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700">
                      <RotateCcw className="h-3.5 w-3.5" /> Retry
                    </button>
                    <button onClick={() => handleDiscardPhoto(p.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">
                      <Trash2 className="h-3.5 w-3.5" /> Discard
                    </button>
                  </div>
                </div>
              ))}
              {itemsLoading ? (
                <div className="text-center py-8 text-gray-500">Loading…</div>
              ) : quarantinedItems.length === 0 && quarantinedPhotos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No failed syncs to review.</div>
              ) : (
                quarantinedItems.map((item) => (
                  <div key={item.id} className="border border-red-100 rounded-lg p-4 bg-red-50/30">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 text-sm">{item.label}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Queued {new Date(item.queued_at).toLocaleString()}
                        </div>
                      </div>
                      {item.failure?.status != null && (
                        <span className="shrink-0 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                          HTTP {item.failure.status}
                        </span>
                      )}
                    </div>
                    {item.failure?.message && (
                      <div className="text-sm text-red-700 bg-white border border-red-100 rounded px-3 py-2 mb-3 font-mono break-all">
                        {item.failure.message}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleRetry(item.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Retry
                      </button>
                      <button
                        onClick={() => handleDiscard(item.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Discard
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="border-t p-4 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
